import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, StatusPill, Table, EmptyState, Input, Select, useToast } from '../../design-system';
import NoticeModal from '../components/NoticeModal';
import { REGIMES, STATUSES, statusLabel, regimeLabel } from '../../lib/notices';
import '../styles/Notices.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

const DUE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: '7d', label: 'Due ≤ 7 days' },
  { value: '30d', label: 'Due ≤ 30 days' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const dueCell = (n) => {
  if (!n.responseDueDate) return '—';
  const due = new Date(n.responseDueDate);
  const open = !['responded', 'closed'].includes(n.status);
  const overdue = open && due < new Date();
  return <span className={overdue ? 'notice-overdue' : undefined}>{fmtDate(due)}</span>;
};

function OrgAdminNotices() {
  const revision = useLiveRefresh();
  const toast = useToast();
  const [notices, setNotices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ regime: '', status: '', due: '', q: '' });
  const [modal, setModal] = useState(null); // null | { mode:'create' } | { mode:'edit'|'view', id }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [list, sum] = await Promise.all([
        apiClient.get('/api/notices', { params }),
        apiClient.get('/api/notices/summary'),
      ]);
      setNotices(list.data);
      setSummary(sum.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.regime, filters.status, filters.due, revision]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const cards = useMemo(
    () => [
      { label: 'Open', value: summary?.open, tone: '' },
      { label: 'Overdue', value: summary?.overdue, tone: 'danger' },
      { label: 'Due ≤ 7 days', value: summary?.dueSoon, tone: 'warning' },
      { label: 'Total', value: summary?.total, tone: '' },
    ],
    [summary]
  );

  const columns = [
    {
      key: 'regime',
      header: 'Regime',
      render: (n) => <StatusPill variant={n.regime === 'gst' ? 'info' : 'neutral'} label={regimeLabel(n.regime)} />,
    },
    {
      key: 'type',
      header: 'Notice',
      render: (n) => (
        <div>
          <span className="notice-type">{n.noticeType}</span>
          {n.section && <span className="notice-section">{n.section}</span>}
        </div>
      ),
    },
    { key: 'client', header: 'Client', render: (n) => n.clientId?.displayName || '—' },
    { key: 'ref', header: 'Ref / DIN', render: (n) => n.referenceNo || '—' },
    { key: 'due', header: 'Response Due', render: dueCell },
    { key: 'assignee', header: 'Owner', render: (n) => n.assigneeId?.name || <span className="notice-muted">Unassigned</span> },
    { key: 'status', header: 'Status', render: (n) => <StatusPill status={n.status} label={statusLabel(n.status)} /> },
  ];

  return (
    <OrgAdminLayout title="Notices" subtitle="Department notices across all clients — never miss a response deadline">
      <div className="notices-page">
        <div className="notice-stats">
          {cards.map((c) => (
            <Card key={c.label} className={`notice-stat${c.tone ? ` ${c.tone}` : ''}`}>
              <div className="notice-stat-num">{c.value ?? 0}</div>
              <div className="notice-stat-label">{c.label}</div>
            </Card>
          ))}
        </div>

        <Card className="notices-toolbar-card">
          <div className="notices-toolbar">
            <Select aria-label="Regime" value={filters.regime} onChange={(e) => setFilters((f) => ({ ...f, regime: e.target.value }))}>
              <option value="">All regimes</option>
              {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </Select>
            <Select aria-label="Status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
            <div className="notice-due-chips">
              {DUE_FILTERS.map((d) => (
                <Button
                  key={d.value || 'all'}
                  size="sm"
                  variant={filters.due === d.value ? 'primary' : 'secondary'}
                  onClick={() => setFilters((f) => ({ ...f, due: d.value }))}
                >
                  {d.label}
                </Button>
              ))}
            </div>
            <form className="notice-search" onSubmit={onSearch}>
              <Input
                aria-label="Search notices"
                placeholder="Search type, ref, authority…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            </form>
            <Button variant="primary" onClick={() => setModal({ mode: 'create' })}>+ Add Notice</Button>
          </div>
        </Card>

        {!loading && notices.length === 0 ? (
          <EmptyState icon="📩" title="No notices" description="Log a department notice to start tracking its response deadline." />
        ) : (
          <Card>
            <Table columns={columns} data={notices} loading={loading} onRowClick={(n) => setModal({ mode: 'view', id: n._id })} />
          </Card>
        )}
      </div>

      {modal && (
        <NoticeModal
          mode={modal.mode}
          noticeId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminNotices;
